import type { AnalysisClient } from '../viewThruBatchView/BatchAnalysis'
import { CLAIM_REASON_LABELS, CLIENT_REASON_LABELS, clientRejectionReasons, reasonLabel } from '../viewThruBatchView/analysisReasons'
import type { ImportWaivers } from './useAllBatchesImport'

export interface WaiverOption {
    key: string
    label: string
    /** Clients (or agreements) in the analysed batches currently failing this check. */
    count: number
}

type Flagged = AnalysisClient & { outcome?: 'eligible' | 'ineligible' | 'duplicate' }

const sortOptions = (options: WaiverOption[]) =>
    options.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

/**
 * The eligibility checks a user can choose to waive, with how many clients or
 * agreements in the analysed batches each one is currently holding back. Every
 * known check is listed even at zero so the choice is stable across runs;
 * dynamic codes the engine produced (valuation failures) are added as seen.
 */
export const buildWaiverOptions = (clients: Flagged[]) => {
    const clientCounts = new Map<string, number>(Object.keys(CLIENT_REASON_LABELS).map((key) => [key, 0]))
    const claimCounts = new Map<string, number>(Object.keys(CLAIM_REASON_LABELS).map((key) => [key, 0]))

    for (const client of clients) {
        // Duplicates are withheld for a different reason and can never be waived.
        if (client.outcome === 'duplicate') continue
        if (client.outcome === 'ineligible') {
            for (const reason of clientRejectionReasons(client)) {
                if (reason === 'unspecified') continue
                clientCounts.set(reason, (clientCounts.get(reason) || 0) + 1)
            }
        }
        for (const claim of client.claims || []) {
            if (claim.duplicateConflict || !claim.claimRejectionReason) continue
            claimCounts.set(claim.claimRejectionReason, (claimCounts.get(claim.claimRejectionReason) || 0) + 1)
        }
    }

    const toOptions = (counts: Map<string, number>) =>
        sortOptions(Array.from(counts.entries()).map(([key, count]) => ({ key, label: reasonLabel(key), count })))

    return { client: toOptions(clientCounts), claim: toOptions(claimCounts) }
}

/**
 * Not-eligible clients whose every recorded failure is waived — the people the
 * preview will now consider. A client with an unwaived check, or with no
 * recorded check at all, stays out.
 */
export const clientsLetThrough = <T extends Flagged>(clients: T[], waivers: ImportWaivers): T[] =>
    clients.filter((client) => {
        if (client.outcome !== 'ineligible') return false
        const reasons = clientRejectionReasons(client).filter((reason) => reason !== 'unspecified')
        return reasons.length > 0 && reasons.every((reason) => waivers.client.includes(reason))
    })

/** Agreements, across every non-duplicate client, that a claim waiver brings back in. */
export const claimsLetThrough = (clients: Flagged[], waivers: ImportWaivers) =>
    clients.reduce((total, client) => {
        if (client.outcome === 'duplicate') return total
        return (
            total +
            (client.claims || []).filter(
                (claim) =>
                    !claim.duplicateConflict &&
                    claim.claimRejectionReason != null &&
                    waivers.claim.includes(claim.claimRejectionReason)
            ).length
        )
    }, 0)

/** "Identity check failed or missing, Previous industry search" for confirm dialogs and summaries. */
export const describeWaivers = (waivers: ImportWaivers) =>
    [...waivers.client, ...waivers.claim].map((reason) => reasonLabel(reason)).join(', ')
